import React, { useContext, useRef, useState } from 'react';
import { UserContextType, useAuth } from '../auth';
import { contentProofreadingService } from '../service/ContentProofreadingService';
import { ProofreadContentType, ProofreadIssue } from '../types/proofread';
import ProofreadReviewDialog from './ProofreadReviewDialog';

type SaveFn = (content: string) => void | Promise<void>;

// Pre-save spell check shared by the custom page and group editors. Same behavior as the
// SendEmail / CreateAnnouncement checks: invisible when clean, fails open if Grok is unreachable,
// and otherwise lets the user apply some, all or none of the fixes before the save goes through.
//
//   const { checkThenSave, isChecking, proofreadDialog } = useProofreadCheck('page', 'page');
//   checkThenSave(body, (finalBody) => save(finalBody));
//   ...render {proofreadDialog} somewhere in the component.
export const useProofreadCheck = (contentType: ProofreadContentType, contentLabel: string) => {
  const { getToken } = useContext<UserContextType>(useAuth);
  const [issues, setIssues] = useState<ProofreadIssue[]>([]);
  const [open, setOpen] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const pending = useRef<{ content: string; save: SaveFn } | null>(null);

  const checkThenSave = async (content: string, save: SaveFn) => {
    if (content.trim().length > 0) {
      setIsChecking(true);
      try {
        const result = await contentProofreadingService.proofread(content, contentType, getToken ? getToken() : '');
        if (result.hasIssues) {
          pending.current = { content, save };
          setIssues(result.issues);
          setOpen(true);
          return;
        }
      } catch (err) {
        // Fail open - if the check itself can't be completed, don't block the save on it.
      } finally {
        setIsChecking(false);
      }
    }

    await save(content);
  };

  const close = () => {
    setOpen(false);
    setIssues([]);
    const current = pending.current;
    pending.current = null;
    return current;
  };

  const handleSaveAnyway = () => {
    const current = close();
    if (current) current.save(current.content);
  };

  const handleApplyAndSave = (selected: ProofreadIssue[]) => {
    const current = close();
    if (!current) return;
    let corrected = current.content;
    selected.forEach((issue) => {
      corrected = corrected.replace(issue.originalText, issue.suggestedText);
    });
    current.save(corrected);
  };

  const proofreadDialog = (
    <ProofreadReviewDialog
      open={open}
      issues={issues}
      contentLabel={contentLabel}
      actionVerb="Save"
      onCancel={close}
      onSendAnyway={handleSaveAnyway}
      onApplyAndSend={handleApplyAndSave}
    />
  );

  return { checkThenSave, isChecking, proofreadDialog };
};
